// externals

// libs/selectors
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as consumerInsightsSelectors from '@makemydeal/dr-dash-store/selectors/consumerInsightsSelectors';
import * as tradeInIntelligenceSelectors from '@makemydeal/dr-dash-store/selectors/tradeInIntelligenceSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// utils
import { formatDollars } from '@makemydeal/dr-common-utils';

// constants
import { DOWN_PAYMENT, MONTHLY_PAYMENT, OFFERING_PRICE, TRADE_ALLOWANCE } from '../../constants';

import DealPulseCard from '../common/DealPulseCard';
import TradeInIntelligenceHeader from '../common/TradeInIntelligenceHeader';
import TradeInIntelligenceRange from '../common/TradeInIntelligenceRange';
import TradeInIntelligenceSeenAnchor from '../common/TradeInIntelligenceSeenAnchor';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';

const DealPulseSection = () => {
    const showDealPulseInManagerView = useTrackedSelector(featureToggleSelectors.isShowDealPulseInManagerViewEnabled, {
        component: 'DealPulseSection',
        field: 'showDealPulseInManagerViewEnabled'
    });

    const offerPrice = useTrackedSelector(offerSelectors.getSellingPrice, { component: 'DealPulseSection', field: 'sellingPrice' });
    const downPayment = useTrackedSelector(offerSelectors.getDownPayment, { component: 'DealPulseSection', field: 'downPayment' });
    const monthlyPayment = useTrackedSelector(offerSelectors.getMonthlyPayment, {
        component: 'DealPulseSection',
        field: 'monthlyPayment'
    });
    const tradeValue = useTrackedSelector(tradeInSelectors.getIncludedTradeInsTotalValue, {
        component: 'DealPulseSection',
        field: 'includedTradeInsTotalValue'
    });
    const dealPulseData = useTrackedSelector(consumerInsightsSelectors.getConsumerInsights, {
        component: 'DealPulseSection',
        field: 'consumerInsights'
    });
    const isTradeInIntelligenceEnabled = useTrackedSelector(featureToggleSelectors.isTradeInIntelligenceEnabled, {
        component: 'DealPulseSection',
        field: 'tradeInIntelligenceEnabled'
    });
    const tradeInIntelligenceRowState = useTrackedSelector(tradeInIntelligenceSelectors.getTradeInIntelligenceRowState, {
        component: 'DealPulseSection',
        field: 'tradeInIntelligenceRowState'
    });

    // Guard: respect dealer toggle and hide when no consumer insights
    // hide when no customer insights data is available.
    if (!showDealPulseInManagerView || !Object.keys(dealPulseData).length) {
        return null;
    }

    const safeMoney = (value: any) => {
        if (isNaN(value) || value <= 0) return '--';
        return formatDollars(value);
    };

    const range = (low?: number, high?: number) => `${safeMoney(low)} - ${safeMoney(high)}`;

    const offerPriceRange = range(dealPulseData.preferredPriceLow, dealPulseData.preferredPriceHigh);
    const downPaymentRange = range(dealPulseData.preferredDownLow, dealPulseData.preferredDownHigh);
    const monthlyPaymentRange = range(dealPulseData.preferredMonthlyLow, dealPulseData.preferredMonthlyHigh);
    const tradeAllowanceRange = range(undefined, undefined);

    const kbb =
        isTradeInIntelligenceEnabled &&
        (tradeInIntelligenceRowState.resolution === 'match' || tradeInIntelligenceRowState.resolution === 'headsUp')
            ? tradeInIntelligenceRowState
            : null;

    const defaultExpanded = kbb !== null || Object.values(dealPulseData).some((val) => typeof val === 'number' && val > 0);

    const dealPulseCardsInfo = [
        {
            title: OFFERING_PRICE,
            offerValue: safeMoney(offerPrice),
            range: offerPriceRange
        },
        {
            title: DOWN_PAYMENT,
            offerValue: safeMoney(downPayment),
            range: downPaymentRange
        },
        {
            title: MONTHLY_PAYMENT,
            offerValue: safeMoney(monthlyPayment),
            range: monthlyPaymentRange
        },
        {
            title: TRADE_ALLOWANCE,
            offerValue: safeMoney(tradeValue),
            range: kbb ? <TradeInIntelligenceRange range={kbb.range} enteredInputs={kbb.enteredInputs} /> : tradeAllowanceRange,
            ...(kbb && {
                header: <TradeInIntelligenceHeader rowState={kbb} />,
                footer: <TradeInIntelligenceSeenAnchor seenAt={kbb.seenAt} />
            })
        }
    ];

    return <DealPulseCard dealPulseCardsInfo={dealPulseCardsInfo} defaultExpanded={defaultExpanded} />;
};

export default DealPulseSection;
